import { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import ProductCard from "../ProductCard/ProductCard";
import { adaptRentalProduct } from "../../utils/rentalProductAdapter";

// API-base
const RENTAL_API_BASE =
  process.env.REACT_APP_RENTAL_API_BASE ||
  "https://spiderlegsproductions.com";

const sectionClass =
  "relative mx-auto my-20 max-w-7xl px-4 sm:px-6 lg:px-8";

const shellClass =
  "overflow-hidden rounded-[2rem] border border-[#DDE7E5] bg-white/90 p-6 shadow-xl backdrop-blur-xl dark:border-darkborder dark:bg-[#111114]/95 sm:p-8 lg:p-10";

const badgeClass =
  "mb-4 inline-flex rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 font-customFont1 text-xs font-extrabold uppercase tracking-[0.14em] !text-[#111827] shadow-sm dark:!text-white";

const titleClass =
  "font-customFont2 text-4xl font-bold uppercase leading-none !text-[#111827] dark:!text-white sm:text-5xl lg:text-6xl";

const introTextClass =
  "mt-4 max-w-3xl font-customFont1 text-base leading-relaxed !text-[#374151] dark:!text-[#D1D5DB] sm:text-lg";

const RelatedProducts = ({ currentProduct }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${RENTAL_API_BASE}/api/products`);

        if (!res.ok) {
          console.error("RelatedProducts: product fetch failed");
          return;
        }

        const data = await res.json();
        const rawList = Array.isArray(data.items) ? data.items : [];
        const adaptedList = rawList.map(adaptRentalProduct);

        setProducts(adaptedList);
      } catch (err) {
        console.error("RelatedProducts fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const related = useMemo(() => {
    if (!currentProduct || products.length === 0) return [];

    const currentCategory = currentProduct.primary_category;
    const name = (currentProduct.name || "").toLowerCase();
    const slug = (currentProduct.slug || "").toLowerCase();

    const isMixer = (product) => {
      const n = (product.name || "").toLowerCase();
      const s = (product.slug || "").toLowerCase();

      return n.includes("mikseri") || s.includes("mikseri");
    };

    const isAudioPackageProduct = (product) => {
      const n = (product.name || "").toLowerCase();
      const s = (product.slug || "").toLowerCase();

      return (
        n.includes("äänentoistopaketti") ||
        n.includes("pa-") ||
        s.includes("aanentoistopaketti") ||
        s.includes("pa-")
      );
    };

    const isDMXController =
      currentCategory === "accessory" &&
      (name.includes("dmx") || slug.includes("dmx") || name.includes("ohjain"));

    const isMicrophone =
      currentCategory === "accessory" &&
      (name.includes("mikrofoni") || slug.includes("mikrofoni"));

    const isCamera =
      currentCategory === "accessory" &&
      (name.includes("kamera") || slug.includes("kamera"));

    const isAudioPackage =
      currentCategory === "audio" && isAudioPackageProduct(currentProduct);

    const recommendationMap = {
      audio: ["accessory", "lighting"],
      lighting: ["accessory", "audio"],
      accessory: ["audio", "lighting"],
    };

    const targetCategories = recommendationMap[currentCategory] || [];

    // 🔒 Fallback: jos kategoriaa ei tunnisteta
    if (!currentCategory) {
      return products
        .filter((p) => p.id !== currentProduct.id)
        .slice(0, 4);
    }

    return products
      .filter((p) => p.id !== currentProduct.id)

      // ❌ Mikseriä ei suositella tällä hetkellä missään
      .filter((p) => !isMixer(p))

      .filter((p) => {
        const pn = (p.name || "").toLowerCase();
        const ps = (p.slug || "").toLowerCase();

        // 🎛️ DMX / valo-ohjain → vain valot
        if (isDMXController) {
          return p.primary_category === "lighting";
        }

        // 🎤 Mikrofoni → PA-paketit + teline, ei PartyBox
        if (isMicrophone) {
          if (pn.includes("partybox") || ps.includes("partybox")) return false;
          if (pn.includes("teline") || ps.includes("teline")) return true;

          return p.primary_category === "audio";
        }

        // 🎥 Kamera → kaikki muut tuotteet sallittu, paitsi mikserit poistettiin jo ylempänä
        if (isCamera) {
          return true;
        }

        // 💡 Valaistus → muut valot + audio + lisälaitteet
        if (currentCategory === "lighting") {
          return ["lighting", "audio", "accessory"].includes(
            p.primary_category
          );
        }

        return targetCategories.includes(p.primary_category);
      })

      // ⛔ PA-paketti → ei toista PA-pakettia eikä mikseriä
      .filter((p) => {
        if (!isAudioPackage) return true;
        return !isAudioPackageProduct(p);
      })

      .sort((a, b) => Number(a.price || 0) - Number(b.price || 0))
      .slice(0, 4);
  }, [products, currentProduct]);

  if (loading || related.length === 0) return null;

  // 🧠 Otsikko sen mukaan mitä oikeasti suositellaan
  const title = (() => {
    const pc = currentProduct.primary_category;

    if (pc === "audio") return "Täydennä settiä";
    if (pc === "lighting") return "Täydennä valaistusta";
    if (pc === "accessory") return "Toimii hyvin tämän kanssa";

    return "Usein vuokrattu yhdessä";
  })();

  const description = (() => {
    const pc = currentProduct.primary_category;

    if (pc === "audio") {
      return "Lisää kokonaisuuteen esimerkiksi valot, mikrofonit tai muut lisälaitteet, joilla saat vuokrauksesta toimivamman tapahtumaa varten.";
    }

    if (pc === "lighting") {
      return "Valaistuksen rinnalle sopii usein äänentoistoa, ohjausta tai muita lisälaitteita, joilla kokonaisuus toimii paremmin paikan päällä.";
    }

    if (pc === "accessory") {
      return "Näitä tuotteita vuokrataan usein samaan tapahtumaan tai samaan tekniseen kokonaisuuteen.";
    }

    return "Katso myös muita tuotteita, jotka voivat sopia samaan vuokraukseen.";
  })();

  return (
    <section className={sectionClass} aria-labelledby="related-products-title">
      <div className={shellClass}>
        <div className="mb-9 text-center">
          <span className={badgeClass}>Suositukset</span>

          <h2 id="related-products-title" className={titleClass}>
            {title}
          </h2>

          <p className={`${introTextClass} mx-auto`}>
            {description}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 mdd:grid-cols-4">
          {related.map((item) => (
            <ProductCard
              key={item.id}
              item={item}
              hideCartButtons={true}
              hideQuantitySelector={true}
              hideAvailableCount={true}
              cartItems={[]}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

RelatedProducts.propTypes = {
  currentProduct: PropTypes.shape({
    id: PropTypes.number.isRequired,
    category: PropTypes.string,
    primary_category: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
  }).isRequired,
};

export default RelatedProducts;